import { Platform, useWindowDimensions } from "react-native";

import { useFlags } from "../../../utils/launchDarkly";
import {
  ROUTES_ALWAYS_HIDDEN,
  ROUTES_HIDDEN_BELOW_BREAKPOINT,
  ROUTES_WITHOUT_TOPBAR,
  useClubStackDeepestRouteName,
} from "./clubStackRoute";

export type PatronTopbarMobileNavSlot =
  | "embedded-topbar"
  | "legacy-navigation-bar"
  | "none";

export type UseShowPageHeaderOptions = {
  preferLegacyNavigationBar?: boolean;
  /**
   * The Club stack is navigating through Home to one of its nested tabs, but
   * HomeTabsNavigator has not attached its state yet.
   */
  isNavigatingToNestedHomeScreen?: boolean;
};

export type PageHeaderLayout = {
  /** Whether the fixed Topbar in PageWrapper should render (caller still gates on `newTopbar`). */
  showFixedTopbar: boolean;
  mobileNavSlot: PatronTopbarMobileNavSlot;
  navigationBarVariant: "topbar" | "default";
};

/**
 * Single source of truth for Topbar placement: fixed PageWrapper header vs
 * embedded in MobileHeader vs legacy MobileNavigationBar only.
 */
export const useShowPageHeader = (
  options?: UseShowPageHeaderOptions
): PageHeaderLayout => {
  const { newTopbar } = useFlags();
  const { width: layoutWidth } = useWindowDimensions();
  const currentRoute = useClubStackDeepestRouteName();

  // On web, prefer the real viewport for breakpoint math. `useWindowDimensions`
  // can lag behind a resized browser (e.g. Playwright), so Agenda would not hide
  // the global Topbar below agendaSplit when it should.
  const width =
    Platform.OS === "web" && typeof window !== "undefined"
      ? window.innerWidth
      : layoutWidth;

  const isRouteAlwaysHidden =
    currentRoute !== null &&
    ROUTES_ALWAYS_HIDDEN.has(currentRoute) &&
    !(currentRoute === "Home" && options?.isNavigatingToNestedHomeScreen);
  const breakpointPx =
    currentRoute !== null
      ? ROUTES_HIDDEN_BELOW_BREAKPOINT[currentRoute]
      : undefined;
  const isRouteHiddenBelowBreakpoint =
    breakpointPx !== undefined && width < breakpointPx;

  const showFixedTopbar = !isRouteAlwaysHidden && !isRouteHiddenBelowBreakpoint;

  const isRouteWithoutTopbar =
    currentRoute !== null && ROUTES_WITHOUT_TOPBAR.has(currentRoute);
  const isUnresolvedHomeRoute =
    currentRoute === "Home" && !options?.isNavigatingToNestedHomeScreen;

  if (!newTopbar || options?.preferLegacyNavigationBar) {
    return {
      showFixedTopbar,
      mobileNavSlot: "legacy-navigation-bar",
      navigationBarVariant: "default",
    };
  }

  if (isRouteWithoutTopbar) {
    return {
      showFixedTopbar,
      mobileNavSlot: "legacy-navigation-bar",
      navigationBarVariant: "default",
    };
  }

  // The fixed header intentionally stays hidden until HomeTabsNavigator
  // resolves MainHome. Do not replace it with the embedded Topbar during that
  // brief state: doing so would add a second Topbar while Club is navigating
  // to a nested Home screen.
  if (isUnresolvedHomeRoute) {
    return {
      showFixedTopbar,
      mobileNavSlot: "none",
      navigationBarVariant: "default",
    };
  }

  if (showFixedTopbar) {
    return {
      showFixedTopbar,
      mobileNavSlot: "none",
      navigationBarVariant: "default",
    };
  }

  return {
    showFixedTopbar,
    mobileNavSlot: "embedded-topbar",
    navigationBarVariant: "topbar",
  };
};
